/**
 * AuthProvider - Global Authentication Context
 * Manages user authentication state across the entire app
 */
import React, { createContext, useState, useEffect, useContext } from 'react';
import { getCurrentUser, getToken, logout as logoutService, getMe } from '../services/authService';

const AuthContext = createContext();

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * AuthProvider Component
 * Wraps the app and provides authentication state
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialStatus, setTrialStatus] = useState(null);

  // DEV bypass — skip the login flow entirely when REACT_APP_DEV_AUTH_BYPASS=true.
  // Original login code below is preserved and runs normally when the flag is off.
  // Never honoured in a production build (`npm run build` always sets NODE_ENV=production), so a
  // stray REACT_APP_DEV_AUTH_BYPASS in an .env file can't ship a stub-admin UI.
  const devAuthBypass =
    process.env.NODE_ENV !== 'production' &&
    (process.env.REACT_APP_DEV_AUTH_BYPASS || '').toLowerCase() === 'true';

  // Seed the bypass token SYNCHRONOUSLY (before any effect fires) so child
  // components can call getToken() immediately on their first render.
  if (devAuthBypass && typeof window !== 'undefined' && !localStorage.getItem('token')) {
    const stub = {
      id: 'dev-admin',
      email: 'dev-admin@carbonroute.local',
      full_name: 'Dev Admin (bypass)',
      is_verified: true,
      is_admin: true,
    };
    localStorage.setItem('token', 'dev-bypass');
    localStorage.setItem('user', JSON.stringify(stub));
  }

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (devAuthBypass) {
        // Inject a stub admin user so ProtectedRoute passes. Backend returns the
        // real DB user for every request (any token works because bypass mode
        // accepts missing Authorization headers), so user_id on persisted rows
        // is still valid.
        const stubUser = {
          id: 'dev-admin',
          email: 'dev-admin@carbonroute.local',
          full_name: 'Dev Admin (bypass)',
          is_verified: true,
          is_admin: true,
        };
        setUser(stubUser);
        // Seed a placeholder token so axios interceptors that attach Bearer
        // headers don't drop the request; the backend ignores it in bypass mode.
        if (!localStorage.getItem('token')) {
          localStorage.setItem('token', 'dev-bypass');
          localStorage.setItem('user', JSON.stringify(stubUser));
        }
        setLoading(false);
        return;
      }

      const token = getToken();
      const savedUser = getCurrentUser();

      if (token && savedUser) {
        setUser(savedUser);

        // Fetch fresh user data from server
        try {
          const freshUser = await getMe();
          setUser(freshUser);
          if (freshUser.trial_status) {
            setTrialStatus(freshUser.trial_status);
          }
        } catch (error) {
          console.error('Failed to fetch user data:', error);
          // Keep using saved user if fetch fails
        }
      } else {
        // No token - user needs to login
        setUser(null);
      }

      setLoading(false);
    };

    initializeAuth();
  }, [devAuthBypass]);

  /**
   * Login function - updates auth state
   */
  const login = (userData, token) => {
    setUser(userData);
    if (userData.trial_status) {
      setTrialStatus(userData.trial_status);
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  /**
   * Logout function - clears auth state
   */
  const logout = () => {
    setUser(null);
    setTrialStatus(null);
    logoutService();
  };

  /**
   * Update user data (e.g., after profile update)
   */
  const updateUser = (userData) => {
    setUser(userData);
    if (userData.trial_status) {
      setTrialStatus(userData.trial_status);
    }
    localStorage.setItem('user', JSON.stringify(userData));
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    try {
      const freshUser = await getMe();
      setUser(freshUser);
      if (freshUser.trial_status) {
        setTrialStatus(freshUser.trial_status);
      }
      return freshUser;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    trialStatus,
    login,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
    isVerified: user?.is_verified || false,
    isAdmin: user?.is_admin || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
